/* ============================================
   PostDetailModal — Read-only post detail view
   Shows full post info: network, type, caption,
   hashtags, storyboard, schedule.
   ============================================ */
import type { SocialPost, SocialChannel } from '@/lib/types'
import { IconX } from '@/components/icons/Icons'
import { STATUS_CONFIG, CHANNEL_COLORS } from '@/components/social-media/PostCard'

interface PostDetailModalProps {
  post: SocialPost
  onClose: () => void
  onEdit?: (post: SocialPost) => void
}

const CHANNEL_NAMES: Record<string, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  twitter: 'X (Twitter)',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  pinterest: 'Pinterest',
}

const POST_TYPE_LABELS: Record<string, { label: string; icon: string }> = {
  image: { label: 'Imagen', icon: 'M21 15V19A2 2 0 0 1 19 21H5A2 2 0 0 1 3 19V15M17 8L12 3L7 8M12 3V15' },
  carousel: { label: 'Carrusel', icon: 'M2 6H6V2M18 6H22V2M2 18H6V22M18 18H22V22M8 4H16M8 20H16M4 8V16M20 8V16' },
  reel: { label: 'Reel / Video corto', icon: 'M23 7L16 12L23 17V7M1 5H15C15.55 5 16 5.45 16 6V18C16 18.55 15.55 19 15 19H1V5Z' },
  story: { label: 'Story', icon: 'M12 2A10 10 0 1 0 12 22A10 10 0 1 0 12 2M12 6A6 6 0 1 1 12 18A6 6 0 1 1 12 6' },
  video: { label: 'Video', icon: 'M23 7L16 12L23 17V7M1 5H15C15.55 5 16 5.45 16 6V18C16 18.55 15.55 19 15 19H1V5Z' },
  text: { label: 'Texto', icon: 'M14 2H6A2 2 0 0 0 4 4V20A2 2 0 0 0 6 22H18A2 2 0 0 0 20 4V8L14 2M16 13H8M16 17H8M10 9H8' },
}

function ChannelIcon({ channel }: { channel: string }) {
  const color = CHANNEL_COLORS[channel as SocialChannel] || '#888'
  switch (channel) {
    case 'instagram':
      return <svg width="18" height="18" viewBox="0 0 24 24" fill={color}><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
    case 'facebook':
      return <svg width="18" height="18" viewBox="0 0 24 24" fill={color}><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
    case 'twitter':
      return <svg width="18" height="18" viewBox="0 0 24 24" fill={color}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
    case 'tiktok':
      return <svg width="18" height="18" viewBox="0 0 24 24" fill={color || '#000'}><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1 0-5.78 2.92 2.92 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 3 15.57 6.33 6.33 0 0 0 9.37 22a6.33 6.33 0 0 0 6.33-6.33V9.39a8.16 8.16 0 0 0 4.29 1.2v-3.4a4.85 4.85 0 0 1-.4-.5z"/></svg>
    case 'linkedin':
      return <svg width="18" height="18" viewBox="0 0 24 24" fill="#0A66C2"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
    default:
      return <span className="sm-post-card__dot" style={{ background: color, width: 14, height: 14 }} />
  }
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Sin fecha'
  return new Date(dateStr).toLocaleDateString('es-PE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Lima',
  })
}

export default function PostDetailModal({ post, onClose, onEdit }: PostDetailModalProps) {
  const statusCfg = STATUS_CONFIG[post.status] ?? STATUS_CONFIG.draft
  const postType = POST_TYPE_LABELS[post.post_type] || POST_TYPE_LABELS.image

  // Parse storyboard from notes
  const storyboard = post.notes?.includes('Storyboard:')
    ? post.notes.split('Storyboard:')[1]?.split('|')[0]?.trim()
    : null

  const adaptationNotes = post.notes?.includes('Adaptación por red:') || post.notes?.includes('Adaptacion por red:')
    ? (post.notes.split(/Adaptaci[oó]n por red:/)[1]?.trim() || null)
    : null

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content post-detail-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="post-detail-modal__header">
          <div className="post-detail-modal__title-row">
            <div className="post-detail-modal__type">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d={postType.icon} />
              </svg>
              <span>{postType.label}</span>
            </div>
            <span className={`sm-badge ${statusCfg.className}`}>
              {statusCfg.label}
            </span>
          </div>
          <button type="button" className="modal-close" onClick={onClose}>
            <IconX size={18} />
          </button>
        </div>

        {/* Networks */}
        <div className="post-detail-modal__section">
          <label className="post-detail-modal__label">Redes sociales</label>
          <div className="post-detail-modal__channels">
            {(post.channels ?? []).map(ch => (
              <div key={ch} className="post-detail-modal__channel">
                <ChannelIcon channel={ch} />
                <span>{CHANNEL_NAMES[ch] || ch}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Schedule */}
        <div className="post-detail-modal__section">
          <label className="post-detail-modal__label">Publicación</label>
          <div className="post-detail-modal__date">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <span>{formatDate(post.publication_date)}</span>
          </div>
        </div>

        {/* Caption */}
        <div className="post-detail-modal__section">
          <label className="post-detail-modal__label">Texto / Caption</label>
          <div className="post-detail-modal__caption">
            {post.caption?.split('\n').map((line, i) => (
              <span key={i}>{line}{i < (post.caption?.split('\n').length || 1) - 1 && <br />}</span>
            )) || '(Sin texto)'}
          </div>
        </div>

        {/* Hashtags */}
        {post.hashtags.length > 0 && (
          <div className="post-detail-modal__section">
            <label className="post-detail-modal__label">Hashtags</label>
            <div className="post-detail-modal__hashtags">
              {post.hashtags.map((tag, i) => (
                <span key={i} className="post-detail-modal__tag">{tag}</span>
              ))}
            </div>
          </div>
        )}

        {/* Storyboard */}
        {storyboard && (
          <div className="post-detail-modal__section">
            <label className="post-detail-modal__label">Storyboard</label>
            <div className="post-detail-modal__storyboard">
              {storyboard}
            </div>
          </div>
        )}

        {/* Adaptation notes */}
        {adaptationNotes && (
          <div className="post-detail-modal__section">
            <label className="post-detail-modal__label">Adaptación por red</label>
            <div className="post-detail-modal__storyboard">
              {adaptationNotes}
            </div>
          </div>
        )}

        {/* Notes */}
        {post.notes && !storyboard && !adaptationNotes && (
          <div className="post-detail-modal__section">
            <label className="post-detail-modal__label">Notas</label>
            <div className="post-detail-modal__storyboard">
              {post.notes}
            </div>
          </div>
        )}

        {/* Image Prompt */}
        {post.image_prompt && (
          <div className="post-detail-modal__section">
            <label className="post-detail-modal__label">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4, verticalAlign: 'middle' }}>
                <path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/><path d="M2 2l7.586 7.586"/>
              </svg>
              Prompt de imagen
            </label>
            <div className="post-detail-modal__image-prompt">
              {post.image_prompt}
            </div>
          </div>
        )}

        {/* Footer actions */}
        <div className="post-detail-modal__footer">
          {post.ai_generated && (
            <div className="post-detail-modal__ai-badge">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
              </svg>
              Generado por Marco (IA)
            </div>
          )}
          {onEdit && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => { onEdit(post); onClose() }}
              style={{ marginLeft: 'auto' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}>
                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
              </svg>
              Editar
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export { ChannelIcon, CHANNEL_NAMES, POST_TYPE_LABELS }
